import { Form, Modal, Select } from "antd"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"

import { INVOICE_STATUSES_MAP } from "../../Project/ProjectViewPage/constants/invoice-statuses.ts"
import { INVOICE_STATUSES } from "../types/invoices.types.ts"

interface ChangeStatusFormValues {
  status: number
}

interface ChangeInvoiceStatusModalProps {
  open: boolean
  currentStatus?: number
  loading?: boolean
  onConfirm: (status: number) => void
  onCancel: () => void
}

const TARGET_STATUSES = [
  INVOICE_STATUSES.ISSUED,
  INVOICE_STATUSES.PARTIALLY_PAID,
  INVOICE_STATUSES.PAID,
  INVOICE_STATUSES.OVERDUE
]

export const ChangeInvoiceStatusModal = ({
  open,
  currentStatus,
  loading,
  onConfirm,
  onCancel
}: ChangeInvoiceStatusModalProps) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<ChangeStatusFormValues>()

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        status: TARGET_STATUSES.includes(
          currentStatus as (typeof TARGET_STATUSES)[number]
        )
          ? (currentStatus as number)
          : undefined
      })
    }
  }, [open, currentStatus, form])

  const options = TARGET_STATUSES.map((status) => ({
    value: status,
    label: INVOICE_STATUSES_MAP[status].label
  }))

  const handleOk = async () => {
    // Modal.onOk discards the returned promise, so a validateFields()
    // rejection would escape as an unhandled rejection.
    try {
      const values = await form.validateFields()
      onConfirm(values.status)
    } catch {
      // Field-level errors are rendered by the Form.
    }
  }

  return (
    <Modal
      open={open}
      title={t("invoices.changeStatus.title")}
      okText={t("invoices.changeStatus.ok")}
      cancelText={t("invoices.changeStatus.cancel")}
      confirmLoading={loading}
      onOk={handleOk}
      onCancel={onCancel}
      destroyOnClose
    >
      <Form<ChangeStatusFormValues> form={form} layout="vertical">
        <Form.Item<ChangeStatusFormValues>
          name="status"
          label={t("invoices.changeStatus.label")}
          rules={[
            { required: true, message: t("invoices.changeStatus.required") }
          ]}
        >
          <Select
            placeholder={t("invoices.changeStatus.placeholder")}
            options={options}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
